import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import { STRIPE_PUBLISHABLE_KEY } from '../config/stripe';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'https://snatchedlatinas.ch/api';

export interface BillingDetails {
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface CartItemForOrder {
  product_id: string;
  quantity: number;
  product?: { name: string; price: number; image_url: string };
  name?: string;
  price?: number;
  image?: string;
}

interface PaymentFormProps {
  total: number;
  onSuccess: (paymentId: string) => void;
  onError: (error: string) => void;
  billingDetails: BillingDetails;
  items?: CartItemForOrder[];
  shipping?: number;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ total, onSuccess, onError, billingDetails, items = [], shipping = 0 }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const { t } = useTranslation();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    try {
      // 1. Create Payment Intent via Express backend
      const res = await fetch(`${BACKEND_URL}/payment/create-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: total,
          currency: 'chf',
          metadata: {
            customer_name: billingDetails.name,
            customer_email: billingDetails.email,
            customer_phone: billingDetails.phone,
            orderId: `ORDER-${Date.now()}`
          }
        })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || t('checkout.paymentError'));
      }

      const { clientSecret, paymentIntentId } = await res.json();

      // 2. Confirm payment with Stripe, passing billing details
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error(t('checkout.cardElementNotFound'));
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: billingDetails.name,
            email: billingDetails.email,
            phone: billingDetails.phone,
            address: {
              line1: billingDetails.address,
              city: billingDetails.city,
              state: billingDetails.state,
              postal_code: billingDetails.zipCode,
              country: 'CH'
            }
          }
        }
      });

      if (error) {
        throw new Error(error.message || t('checkout.paymentError'));
      }

      if (paymentIntent?.status === 'succeeded') {
        // 3. Confirm order in backend (save to Supabase)
        await fetch(`${BACKEND_URL}/payment/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            paymentIntentId: paymentIntentId || paymentIntent.id,
            customerEmail: billingDetails.email,
            orderDetails: {
              name: billingDetails.name,
              phone: billingDetails.phone,
              address: billingDetails.address,
              city: billingDetails.city,
              state: billingDetails.state,
              zipCode: billingDetails.zipCode,
              amount: total,
              shipping,
              items
            }
          })
        });

        onSuccess(paymentIntent.id);
      } else {
        throw new Error(t('checkout.paymentNotProcessed'));
      }

    } catch (err: any) {
      onError(err.message || t('checkout.paymentError'));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border border-gray-300 rounded-lg">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
            },
          }}
        />
      </div>
      
      <button
        type="submit"
        disabled={!stripe || processing}
        className="w-full bg-gradient-to-r from-cyan-400 to-green-500 text-white py-3 rounded-lg font-bold hover:from-cyan-500 hover:to-green-600 disabled:opacity-50 transition-all duration-200"
      >
        {processing ? t('checkout.processing') : `${t('checkout.pay')} CHF ${total.toFixed(2)}`}
      </button>
    </form>
  );
};

interface StripePaymentProps {
  total: number;
  onSuccess: (paymentId: string) => void;
  onError: (error: string) => void;
  billingDetails: BillingDetails;
  items?: CartItemForOrder[];
  shipping?: number;
}

const StripePayment: React.FC<StripePaymentProps> = ({ total, onSuccess, onError, billingDetails, items = [], shipping = 0 }) => {
  return (
    <Elements stripe={stripePromise} options={{ locale: 'de' }}>
      <PaymentForm total={total} onSuccess={onSuccess} onError={onError} billingDetails={billingDetails} items={items} shipping={shipping} />
    </Elements>
  );
};

export default StripePayment;
